import {
  Box,
  IconButton,
  ImageList,
  ImageListItem,
  ListItem,
  Skeleton,
  Stack,
  Typography,
  styled,
} from '@mui/material'
import CloseIcon from 'components/shared/icons/CloseIcon'
import { CellsByGroup } from 'redux/slices'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useEventManager from 'redux/slices/hooks/useEventsManager'
import { DeepcellPrimaryButton } from 'components/shared'
import { useState } from 'react'
import DropdownModal from 'components/runs/DropdownModal'
import useGlobalDownloadTrigger from 'utils/useGlobalDownloadTrigger'
import useRateLimiter from 'utils/useRateLimiter'
import {
  generateCellImagesZipWithFolders,
  generateZipFileName,
  parseCellId,
  ZipFolderStructure,
} from 'utils/zipGenerator'
import { useCellImageUrlFromCellId } from '../tsv/utils'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'

const styleProps = {
  width: '43.55px',
  height: '44px',
  borderRadius: '4px',
}

const HeaderLabel = styled(Typography)({
  fontSize: '16px',
  lineHeight: '16.96px',
  letterSpacing: '0.1em',
  textAlign: 'left',
})

const MorphotypeCardContainer = styled(Box)({
  border: '0.5px solid #D1D1D1',
  width: '275px',
  minHeight: '87px',
  maxHeight: '134px',
  padding: '10px',
})

const MorphotypeGroupLabel = styled(Typography)({
  width: '100%',
  fontSize: '12px',
  lineHeight: '12.72px',
  textAlign: 'left',
  color: '#172623',
})

const StyledListItem = styled(ListItem)({
  color: '#000',
  fontSize: '12px',
  fontWeight: 'bold',
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  '&:hover': {
    background: '#E6E4FF',
  },
  '&:active': {
    color: '#5F55D1',
    background: '#E6E4FF',
  },
  '& > a': {
    textDecoration: 'none',
    color: '#000',
  },
})

const CellImage = ({ cell }: { cell: string }): JSX.Element => {
  const { imageDataUrl } = useCellImageUrlFromCellId(cell)

  return (
    <ImageListItem>
      {imageDataUrl ? (
        <img style={styleProps} data-testid="plotCellImageImgElement" src={imageDataUrl} alt="" />
      ) : (
        <Skeleton data-testid="plotCellImageSkeleton" variant="rectangular" sx={styleProps} />
      )}
    </ImageListItem>
  )
}

const MorphotypeCard = ({ morphoGroupData }: { morphoGroupData: CellsByGroup }): JSX.Element => {
  const { cellIds, groupName } = morphoGroupData
  return (
    <MorphotypeCardContainer>
      <Stack gap="10px">
        <MorphotypeGroupLabel>{groupName}</MorphotypeGroupLabel>
        <ImageList sx={{ maxHeight: '96px', margin: '0px' }} cols={5} rowHeight={44}>
          {cellIds.map((cell) => {
            return <CellImage key={cell} cell={cell} />
          })}
        </ImageList>
      </Stack>
    </MorphotypeCardContainer>
  )
}

const ExportMorphotypeContent = (): JSX.Element => {
  const [modalState, setModalState] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const {
    clearExportMorphotype,
    cellVisualizations: { exportCellMorphotype },
  } = useCellVisualizationsSlice()
  const events = useEventManager()
  const { sessionId } = useCellVisualizationUrlParams()

  const { triggerSyncDownload } = useGlobalDownloadTrigger()

  // Rate limiters for morphotype downloads
  const pngRateLimiter = useRateLimiter({
    key: 'morphotype-png-download',
    maxDownloads: 10,
    timeFrameMinutes: 10,
  })

  const tiffRateLimiter = useRateLimiter({
    key: 'morphotype-tiff-download',
    maxDownloads: 10,
    timeFrameMinutes: 10,
  })

  const handleExport = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    setModalState(!modalState)
  }

  const handleImageExport = async (fileType: 'png' | 'tiff') => {
    const rateLimiter = fileType === 'png' ? pngRateLimiter : tiffRateLimiter

    // Track cell visualization export
    events.sendCellVisualizationExport({
      sessionId,
      morphotypes: exportCellMorphotype.map((group) => ({
        name: group.groupName,
        cellIds: group.cellIds,
      })),
      exportType: fileType,
    })

    await rateLimiter.checkAndExecute(async () => {
      // Generate filename with morphotype names
      const morphotypeNames = exportCellMorphotype
        .map((group) => group.groupName)
        .join('-')
      const prefix = `${morphotypeNames}-${fileType.toUpperCase()}`
      const fileName = generateZipFileName(prefix)

      // Convert morphotype groups to folder structure
      const folderStructure: ZipFolderStructure[] = exportCellMorphotype.map((group) => ({
        folderName: group.groupName,
        cells: group.cellIds.map((cellId) => parseCellId(cellId)),
      }))

      await triggerSyncDownload({
        type: fileType === 'png' ? 'morphotype-png' : 'morphotype-tiff',
        fileName,
        generateFile: async () => {
          const blob = await generateCellImagesZipWithFolders(folderStructure, fileType)
          return blob
        },
      })
    })

    // Close the modal
    setModalState(false)
  }

  return (
    <>
      <Stack sx={{ height: '100%', position: 'relative' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <HeaderLabel>Export Selections ({exportCellMorphotype.length})</HeaderLabel>
          <IconButton sx={{ p: 0 }}>
            <CloseIcon onClick={() => clearExportMorphotype()} />
          </IconButton>
        </Stack>
        <Stack sx={{ flexGrow: 1, overflowY: 'auto' }} mt="16px" gap="10px">
          {exportCellMorphotype.map((morpho) => {
            return morpho.cellIds.length ? (
              <MorphotypeCard key={morpho.groupId} morphoGroupData={morpho} />
            ) : (
              <></>
            )
          })}
        </Stack>
        <Stack sx={{ paddingY: '20px' }}>
          <DeepcellPrimaryButton onClick={handleExport} contained type="button">
            Export
          </DeepcellPrimaryButton>
        </Stack>
      </Stack>

      <DropdownModal
        placement="top-start"
        disablePortal={false}
        transition
        anchorEl={anchorEl}
        open={modalState}
        onClickAway={() => setModalState(false)}
      >
        <Box sx={{ width: anchorEl?.clientWidth || '118px' }}>
          <StyledListItem
            onClick={() => {
              handleImageExport('png')
            }}
          >
            PNG
          </StyledListItem>
          <StyledListItem
            onClick={() => {
              handleImageExport('tiff')
            }}
          >
            TIFF
          </StyledListItem>
        </Box>
      </DropdownModal>

      {/* Downloads are now handled by the Enhanced Download UX system */}
    </>
  )
}

export default ExportMorphotypeContent
